/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import React from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  useTheme as useMuiTheme,
  Avatar,
  styled,
  Link as MuiLink,
  createTheme,
  Card,
  CardContent,
  CardActionArea,
} from "@mui/material";
import {
  ArrowBack,
  School,
  VerifiedUser,
  ChevronRight,
  Logout,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import { getUser, logOutUser } from "../../common/utils";

// ============================================
// Modern Dashboard Theme (MUI5) - Matching DeltaGovPortal
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a", // deep navy from spec
    },
    secondary: {
      main: "#ef4444", // red for logout button
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "#1f2937",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: "6px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            borderColor: "#243b5a",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "8px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components - Matching DeltaGovPortal
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  boxSizing: "border-box",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const StyledCard = styled(Card)({
  height: "100%",
  display: "flex",
  flexDirection: "column",
});

const IconWrapper = styled(Box)({
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  backgroundColor: "#243b5a",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#ffffff",
  marginBottom: "16px",
  "& svg": {
    fontSize: "28px",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

// ============================================
// Main SchoolDashboard Component
// ============================================
const SchoolDashboard: React.FC = () => {
  const history = useHistory();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Mock school data - in a real app, this would come from context or API
  const user     = getUser();
  const schoolData = {
    schoolName: user?.schoolName,
    lgaCode: user?.lgaCode,
    schoolCode: user?.schoolCode,
  };

  // Handle navigation to validation page
  const handleValidationClick = () => {
    history.push("/validation-list");
  };

  // Handle navigation to registration page
  const handleRegistrationClick = () => {
    history.push("/registered-students");
  };

  // Handle logout
  const handleLogout = () => {
    // Clear session data here
 logOutUser();
    history.push("/login")
    
  };

  // Handle return to main portal
  const handleReturnToMainPortal = () => {
    history.push("/login");
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          {/* Full page layout */}
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER - Updated with requested layout */}
            <Header>
              <HeaderTopRow>
                {/* Left side - Logo and Ministry info */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      School Portal
                    </Typography>
                  </Box>
                </Box>

                {/* Right side - School info (top row) */}
                <SchoolInfoBox>
                <Typography
  variant="subtitle1"
  sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
>
  {schoolData.schoolName}
</Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    LGA: {schoolData.lgaCode} | Code: {schoolData.schoolCode}
                  </Typography>
                </SchoolInfoBox>
              </HeaderTopRow>

              {/* Bottom row - Logout button aligned right */}
              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>
            </Header>

            {/* MAIN CONTENT */}
            <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
              {/* Return Link */}
              <Button
                onClick={handleReturnToMainPortal}
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#243b5a",
                  fontSize: "13px",
                  textTransform: "none",
                  p: 0,
                  mb: 3,
                  '&:hover': {
                    textDecoration: "underline",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Return to main portal
              </Button>

              {/* Title Section */}
              <Typography variant="h1" sx={{ fontSize: "2rem", fontWeight: 700, color: "#1f2937", mb: 0.5 }}>
                School Portal Services
              </Typography>
              <Typography variant="body1" sx={{ color: "#6b7280", mb: 2 }}>
                Select a service to proceed
              </Typography>

              {/* Info Box */}
              <Paper sx={{ p: 2.5, mb: 4, bgcolor: "#f9fafb" }}>
                <Typography variant="body2" sx={{ color: "#374151" }}>
                  For primary portal access, please visit{" "}
                  <MuiLink 
                    href="http://www.dsgmope.ng" 
                    target="_blank"
                    sx={{ color: "#1d4ed8", textDecoration: "underline", fontWeight: 500 }}
                  >
                    www.dsgmope.ng
                  </MuiLink>
                </Typography>
              </Paper>

              {/* Available Services Label */}
              <Typography variant="caption" sx={{ display: "block", mb: 2.5 }}>
                AVAILABLE SERVICES
              </Typography>

              {/* Services Grid */}
              <Grid container spacing={3}>
                {/* Validation List Card */}
                <Grid item xs={12} sm={6}>
                  <StyledCard>
                    <CardActionArea 
                      onClick={handleValidationClick}
                      sx={{ height: "100%", p: 2 }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <IconWrapper>
                          <VerifiedUser />
                        </IconWrapper>
                        <Typography variant="h5" sx={{ fontSize: "1.125rem", fontWeight: 600, mb: 1, color: "#1f2937" }}>
                          Validation List
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
                          Verify and validate student registration records
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", color: "#243b5a" }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, mr: 0.5 }}>
                            Access Service
                          </Typography>
                          <ChevronRight fontSize="small" />
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </StyledCard>
                </Grid>

                {/* Primary School Registration Card */}
                <Grid item xs={12} sm={6}>
                  <StyledCard>
                    <CardActionArea 
                      onClick={handleRegistrationClick}
                      sx={{ height: "100%", p: 2 }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <IconWrapper>
                          <School />
                        </IconWrapper>
                        <Typography variant="h5" sx={{ fontSize: "1.125rem", fontWeight: 600, mb: 1, color: "#1f2937" }}>
                          Primary School Registration
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
                          Register new students for primary education
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", color: "#243b5a" }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, mr: 0.5 }}>
                            Access Service
                          </Typography>
                          <ChevronRight fontSize="small" />
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </StyledCard>
                </Grid>
              </Grid>
            </Container>

            {/* FOOTER - Matching DeltaGovPortal exactly */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default SchoolDashboard;